import { useEffect, useCallback, useRef, useState } from 'react';

interface UseInfiniteScrollOptions {
  threshold?: number; // Distance from bottom to trigger load (in pixels)
  enabled?: boolean; // Whether infinite scroll is enabled
}

interface UseInfiniteScrollReturn {
  isFetching: boolean;
  setIsFetching: (fetching: boolean) => void;
  observerRef: React.RefObject<HTMLDivElement>;
}

/**
 * Custom hook for implementing infinite scroll functionality.
 * Detects when user scrolls near the bottom and triggers a callback.
 */
export const useInfiniteScroll = (
  fetchMore: () => void | Promise<void>,
  options: UseInfiniteScrollOptions = {}
): UseInfiniteScrollReturn => {
  const {
    threshold = 200, // Default 200px from bottom
    enabled = true
  } = options;

  const [isFetching, setIsFetching] = useState(false);
  const observerRef = useRef<HTMLDivElement>(null);
  const fetchMoreRef = useRef(fetchMore);

  // Update ref when fetchMore changes
  fetchMoreRef.current = fetchMore;

  const handleIntersection = useCallback(
    async (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      
      if (entry.isIntersecting && enabled && !isFetching) {
        setIsFetching(true);
        try {
          await fetchMoreRef.current();
        } catch (error) {
          console.error('Error fetching more data:', error);
        } finally {
          setIsFetching(false);
        }
      }
    },
    [enabled, isFetching]
  );

  useEffect(() => {
    const observer = observerRef.current;
    if (!observer || !enabled) return;

    const intersectionObserver = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: `0px 0px ${threshold}px 0px`, // Trigger when element is within threshold of viewport
      threshold: 0.1
    });

    intersectionObserver.observe(observer);

    return () => {
      if (observer) {
        intersectionObserver.unobserve(observer);
      }
      intersectionObserver.disconnect();
    };
  }, [handleIntersection, threshold, enabled]);

  return {
    isFetching,
    setIsFetching,
    observerRef
  };
};

/**
 * Alternative implementation using scroll event instead of Intersection Observer
 * Useful for more complex scroll detection scenarios
 */
export const useInfiniteScrollWithScrollEvent = (
  fetchMore: () => void | Promise<void>,
  options: UseInfiniteScrollOptions = {}
): UseInfiniteScrollReturn => {
  const {
    threshold = 200,
    enabled = true
  } = options;

  const [isFetching, setIsFetching] = useState(false);
  const observerRef = useRef<HTMLDivElement>(null);
  const fetchMoreRef = useRef(fetchMore);

  // Update ref when fetchMore changes
  fetchMoreRef.current = fetchMore;

  const handleScroll = useCallback(async () => {
    if (!enabled || isFetching) return;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    // Check if we're within threshold pixels of the bottom
    if (scrollTop + windowHeight >= documentHeight - threshold) {
      setIsFetching(true);
      try {
        await fetchMoreRef.current();
      } catch (error) {
        console.error('Error fetching more data:', error);
      } finally {
        setIsFetching(false);
      }
    }
  }, [enabled, isFetching, threshold]);

  useEffect(() => {
    if (!enabled) return;

    // Throttle scroll events
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [handleScroll, enabled]);

  return {
    isFetching,
    setIsFetching,
    observerRef // Not used in scroll event version, but kept for API consistency
  };
};